/**
 * Layout — the authenticated app shell: top nav, active-link highlighting,
 * current user + logout. Admin link only shows for the admin role.
 */
import { useCallback, useEffect, useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { api } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { AskBlipPanel } from './AskBlipPanel';

export function Layout(): JSX.Element {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  // Ask BLIP shows only when the server has it on AND this account may use it
  // (staff roles; never a `client` portal login). The server enforces this too.
  const [askAvailable, setAskAvailable] = useState(false);
  const [askOpen, setAskOpen] = useState(false);
  const closeAsk = useCallback(() => setAskOpen(false), []);
  useEffect(() => {
    if (!user || user.role === 'client') return;
    api.assistant
      .status()
      .then((s) => setAskAvailable(s.enabled && s.allowed))
      .catch(() => setAskAvailable(false));
  }, [user]);

  const onLogout = (): void => {
    logout();
    navigate('/login', { replace: true });
  };

  return (
    <div className="app-shell">
      <header className="topbar">
        <div className="brand">
          <span className="brand-mark">✦</span> Aurora
          <span className="brand-sub">Scientific Lead Intelligence</span>
        </div>
        <nav className="topnav">
          {/* A `client`-portal login (2026-09-01) only sees its own reports —
              everything else here is Aurora-internal (lead intelligence,
              the global supplier dictionary, admin) and the server already
              rejects it regardless; hiding the links avoids confusing 403s. */}
          {user?.role !== 'client' && (
            <>
              <NavLink to="/" end>
                Dashboard
              </NavLink>
              <NavLink to="/upload">Upload</NavLink>
            </>
          )}
          <NavLink to="/benchmarks">Benchmarks</NavLink>
          {user?.role !== 'client' && (
            <>
              <NavLink to="/suppliers">Suppliers</NavLink>
              <NavLink to="/sources">Sources</NavLink>
            </>
          )}
          <NavLink to="/selection-audits">Selection Readiness</NavLink>
          <NavLink to="/content-audits">Content Discoverability</NavLink>
          {user?.role === 'admin' && <NavLink to="/admin" end>Admin</NavLink>}
          {user?.role === 'admin' && askAvailable && (
            <NavLink to="/admin/assistant">Requests</NavLink>
          )}
        </nav>
        {askAvailable && (
          <button
            type="button"
            className={askOpen ? 'btn btn-primary ab-launch' : 'btn ab-launch'}
            onClick={() => setAskOpen((o) => !o)}
          >
            ✦ Ask BLIP
          </button>
        )}
        <div className="user-box">
          <span className="user-name">{user?.fullName}</span>
          <span className="user-role">{user?.role}</span>
          <button type="button" className="btn btn-ghost" onClick={onLogout}>
            Sign out
          </button>
        </div>
      </header>
      <main className="content">
        <Outlet />
      </main>
      {askAvailable && <AskBlipPanel open={askOpen} onClose={closeAsk} />}
      <footer className="footer muted">
        Public scientific data only · Every fact attributed · AI is advisory —
        Aurora never contacts anyone autonomously.
      </footer>
    </div>
  );
}
